import { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { markupBadgeColor } from '@/lib/grades';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ChevronDown, ChevronUp, Map as MapIcon, TrendingDown, TrendingUp, DollarSign, MapPin, Navigation, Loader2, ExternalLink } from 'lucide-react';
import { useBrand } from '@/config/brands';
import { useTheme } from '@/contexts/ThemeContext';
import { useLiveDb } from '@/lib/offlineData';
import { loadScenarioCoverageFor, getCoverageForScenario, getHospitalInfo, haversineDistance, type CoverageRow } from '@/lib/scenarioCoverageStore';
import { blendCoverageRow } from '@/lib/priceSanity';
import { CoverageProgressLoader } from '@/lib/CoverageProgressLoader';
import type { UserLocation } from '@/hooks/useUserLocation';

interface RankedHospital {
  hospital_id: string;
  total_hospital_price: number;
  total_national_price: number;
  avg_markup_percentage: number;
  coverage_percentage: number;
  matched_codes: number;
  total_codes: number;
  blendedPrice: number;
  hospitals: {
    name: string;
    city: string;
    state: string;
  };
}

type TierKey = 'nearby' | 'state' | 'national';

interface TierData {
  label: string;
  hospitals: RankedHospital[];
  cheapest: RankedHospital[];
  mostExpensive: RankedHospital[];
  available: boolean;
  unavailableReason?: string;
}

interface ScenarioRankingsProps {
  scenarioId: string;
  scenarioName: string;
  nationalTotal: number;
  procedureCount: number;
  onHospitalSelect?: (hospitalId: string, hospitalName: string) => void;
  userLocation?: UserLocation | null;
  radiusMiles?: number;
  onDetectLocation?: () => void;
  onZipLookup?: (zip: string) => void;
  isLocating?: boolean;
  onRankingsReady?: (data: { cheapest: { id: string; name: string; city: string; state: string; price: number }[]; mostExpensive: { id: string; name: string; city: string; state: string; price: number }[]; total: number }) => void;
}

export function ScenarioRankings({ scenarioId, scenarioName, nationalTotal, procedureCount, onHospitalSelect, userLocation, radiusMiles = 60, onDetectLocation, onZipLookup, isLocating, onRankingsReady }: ScenarioRankingsProps) {
  const brand = useBrand();
  const isAhp = brand.key === 'ahp';
  const { isDark } = useTheme();
  const ahpDark = isAhp && isDark;
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState<TierKey>('national');
  const liveDb = useLiveDb();

  // Load ALL coverage rows (not just 50+50) so we can filter by tier
  const { data: allRanked = [], isLoading } = useQuery({
    queryKey: ['scenario-rankings-all', scenarioId, nationalTotal, liveDb],
    queryFn: async () => {
      if (!liveDb) {
        const loaded = await loadScenarioCoverageFor(scenarioId);
        if (loaded) {
          const rows = getCoverageForScenario(scenarioId);
          if (rows.length > 0) {
            const results: RankedHospital[] = [];
            for (const row of rows) {
              const hospital = getHospitalInfo(row.h);
              const blended = blendCoverageRow({ hp: row.hp, np: row.np, mc: row.mc, tc: row.tc, pm: row.pm, nationalCost: nationalTotal });
              if (!blended) continue; // garbage/unpriceable row
              const { blendedPrice } = blended;
              results.push({
                hospital_id: row.h,
                total_hospital_price: row.hp,
                total_national_price: row.np,
                avg_markup_percentage: row.mp,
                coverage_percentage: row.cp,
                matched_codes: row.mc,
                total_codes: row.tc,
                blendedPrice,
                hospitals: {
                  name: hospital?.n || 'Unknown',
                  city: hospital?.c || '',
                  state: hospital?.s || '',
                },
              });
            }
            return results;
          }
        }
      }

      // Supabase fallback — fetch coverage without FK join (table may lack FK to hospitals)
      const { data: cheapRaw, error: err1 } = await supabase
        .from('scenario_hospital_coverage')
        .select('hospital_id, total_hospital_price, total_national_price, avg_markup_percentage, coverage_percentage, matched_codes, total_codes')
        .eq('scenario_id', scenarioId)
        .eq('cornerstone_pass', true)
        .gte('coverage_percentage', 50)
        .gt('total_hospital_price', 0)
        .order('total_hospital_price', { ascending: true })
        .limit(50);

      const { data: expRaw, error: err2 } = await supabase
        .from('scenario_hospital_coverage')
        .select('hospital_id, total_hospital_price, total_national_price, avg_markup_percentage, coverage_percentage, matched_codes, total_codes')
        .eq('scenario_id', scenarioId)
        .eq('cornerstone_pass', true)
        .gte('coverage_percentage', 50)
        .gt('total_hospital_price', 0)
        .order('total_hospital_price', { ascending: false })
        .limit(50);

      if (err1 || err2) {
        console.error('Rankings error:', err1 || err2);
        return [];
      }

      // Collect unique hospital IDs and fetch hospital info
      const allRows = [...(cheapRaw || []), ...(expRaw || [])];
      const hospitalIds = [...new Set(allRows.map(r => r.hospital_id))];
      const hospitalMap = new Map<string, { name: string; city: string; state: string }>();

      // Try static cache first, then Supabase for any missing
      for (const hid of hospitalIds) {
        const info = getHospitalInfo(hid);
        if (info?.n) hospitalMap.set(hid, { name: info.n, city: info.c || '', state: info.s || '' });
      }
      const missing = hospitalIds.filter(id => !hospitalMap.has(id));
      if (missing.length > 0) {
        const { data: hData } = await supabase.from('hospitals').select('id, name, city, state').in('id', missing.slice(0, 100));
        for (const h of hData || []) hospitalMap.set(h.id, { name: h.name, city: h.city || '', state: h.state || '' });
      }

      const seen = new Set<string>();
      const merged: RankedHospital[] = [];
      for (const row of allRows) {
        if (seen.has(row.hospital_id)) continue;
        seen.add(row.hospital_id);
        const blended = blendCoverageRow({
          hp: Number(row.total_hospital_price),
          np: Number(row.total_national_price),
          mc: Number(row.matched_codes),
          tc: Number(row.total_codes),
          nationalCost: nationalTotal,
        });
        if (!blended) continue; // garbage/unpriceable row
        const { blendedPrice } = blended;
        merged.push({
          hospital_id: row.hospital_id,
          total_hospital_price: row.total_hospital_price,
          total_national_price: row.total_national_price,
          avg_markup_percentage: row.avg_markup_percentage,
          coverage_percentage: row.coverage_percentage,
          matched_codes: row.matched_codes,
          total_codes: row.total_codes,
          blendedPrice,
          hospitals: hospitalMap.get(row.hospital_id) || { name: 'Unknown', city: '', state: '' },
        });
      }
      return merged;
    },
    enabled: !!scenarioId && nationalTotal > 0,
  });

  // Build distance map for nearby filtering
  const hospitalDistances = useMemo(() => {
    if (!userLocation) return new Map<string, number>();
    const map = new Map<string, number>();
    for (const h of allRanked) {
      const info = getHospitalInfo(h.hospital_id);
      if (info?.la != null && info?.lo != null) {
        map.set(h.hospital_id, haversineDistance(userLocation.lat, userLocation.lng, info.la, info.lo));
      }
    }
    return map;
  }, [allRanked, userLocation]);

  // Compute three tiers
  const tiers = useMemo((): Record<TierKey, TierData> => {
    const sorted = [...allRanked].sort((a, b) => a.blendedPrice - b.blendedPrice);

    // National
    const nationalCheapest = sorted.slice(0, 10);
    const nationalExpensive = sorted.slice(-10).reverse();

    // State
    const userState = userLocation?.state;
    let stateHospitals: RankedHospital[] = [];
    let stateAvailable = !!userState;
    if (userState) {
      stateHospitals = sorted.filter(h => h.hospitals.state === userState);
    }
    const stateCheapest = stateHospitals.slice(0, 10);
    const stateExpensive = stateHospitals.slice(-10).reverse();

    // Nearby
    let nearbyHospitals: RankedHospital[] = [];
    let nearbyAvailable = !!userLocation;
    if (userLocation) {
      nearbyHospitals = sorted.filter(h => {
        const dist = hospitalDistances.get(h.hospital_id);
        return dist != null && dist <= radiusMiles;
      });
    }
    const nearbyCheapest = nearbyHospitals.slice(0, 10);
    const nearbyExpensive = nearbyHospitals.slice(-10).reverse();

    return {
      nearby: {
        label: `Within ${radiusMiles} mi`,
        hospitals: nearbyHospitals,
        cheapest: nearbyCheapest,
        mostExpensive: nearbyExpensive,
        available: nearbyAvailable,
        unavailableReason: !nearbyAvailable ? 'Set your location to see nearby hospitals' : nearbyHospitals.length === 0 ? `No hospitals within ${radiusMiles} miles` : undefined,
      },
      state: {
        label: userState || 'State',
        hospitals: stateHospitals,
        cheapest: stateCheapest,
        mostExpensive: stateExpensive,
        available: stateAvailable,
        unavailableReason: !stateAvailable ? 'Set your location to see state data' : stateHospitals.length === 0 ? `No hospitals in ${userState}` : undefined,
      },
      national: {
        label: 'National',
        hospitals: sorted,
        cheapest: nationalCheapest,
        mostExpensive: nationalExpensive,
        available: true,
      },
    };
  }, [allRanked, userLocation, radiusMiles, hospitalDistances]);

  const hasRankings = tiers.national.cheapest.length > 0;

  // Lift rankings data to parent for JSON-LD structured data
  useEffect(() => {
    if (!onRankingsReady || !hasRankings) return;
    const toSeo = (h: RankedHospital) => ({ id: h.hospital_id, name: h.hospitals.name, city: h.hospitals.city, state: h.hospitals.state, price: h.blendedPrice });
    onRankingsReady({
      cheapest: tiers.national.cheapest.map(toSeo),
      mostExpensive: tiers.national.mostExpensive.map(toSeo),
      total: tiers.national.hospitals.length,
    });
  }, [hasRankings, tiers.national.cheapest, tiers.national.mostExpensive, tiers.national.hospitals.length]);

  const handleClick = (h: RankedHospital) => {
    if (onHospitalSelect) {
      onHospitalSelect(h.hospital_id, h.hospitals.name);
    }
  };

  // Active tab data for expanded view
  const activeTier = tiers[activeTab];

  return (
    <Card
      className={isAhp ? '' : 'border-blue-400/20 bg-black/40 backdrop-blur-sm'}
      style={isAhp ? { backgroundColor: ahpDark ? '#0f1d2f' : '#fff', border: ahpDark ? '1px solid rgba(197,165,90,0.2)' : '1px solid rgba(27,27,27,0.12)', boxShadow: ahpDark ? '0 2px 16px rgba(0,0,0,0.4)' : '0 2px 16px rgba(0,0,0,0.06)' } : undefined}
    >
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle
            className={isAhp ? 'text-lg flex items-center gap-2' : 'text-lg font-mono text-blue-400 flex items-center gap-2'}
            style={isAhp ? { fontFamily: "'Instrument Serif', Georgia, serif", color: ahpDark ? '#7da6d4' : '#002868', fontWeight: 400 } : undefined}
          >
            <DollarSign className="w-5 h-5" style={isAhp ? { color: ahpDark ? '#7da6d4' : '#002868' } : undefined} />
            Price Comparison
          </CardTitle>
          {hasRankings && !isLoading && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setExpanded(!expanded)}
              className="text-xs gap-1 shrink-0"
            >
              {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
              {expanded ? 'Collapse' : 'Top 10'}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {/* National average total */}
        <div className="flex items-center gap-3 mb-3">
          <span
            className={isAhp ? 'text-2xl font-bold' : 'text-2xl font-bold text-blue-400 font-mono'}
            style={isAhp ? { color: ahpDark ? '#e85d6c' : '#B22234', fontFamily: "'Instrument Serif', Georgia, serif" } : undefined}
          >
            National Avg: ${nationalTotal.toLocaleString()}
          </span>
          <span className="text-xs text-muted-foreground">
            from {procedureCount} procedures
          </span>
        </div>

        {isLoading && <CoverageProgressLoader />}

        {/* Collapsed: 3-tier summary cards */}
        {!isLoading && hasRankings && !expanded && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {(['nearby', 'state', 'national'] as TierKey[]).map(tier => (
              <TierSummaryCard
                key={tier}
                tier={tiers[tier]}
                isAhp={isAhp}
                ahpDark={ahpDark}
                nationalTotal={nationalTotal}
                onHospitalClick={handleClick}
                onDetectLocation={tier !== 'national' ? onDetectLocation : undefined}
                onZipLookup={tier !== 'national' ? onZipLookup : undefined}
                isLocating={isLocating}
              />
            ))}
          </div>
        )}

        {/* Expanded: tabbed view */}
        {expanded && (
          <div className="mt-3">
            {/* Pill tabs */}
            <div className="flex gap-1.5 mb-3">
              {(['nearby', 'state', 'national'] as TierKey[]).map(tier => {
                const t = tiers[tier];
                const isActive = activeTab === tier;
                return (
                  <button
                    key={tier}
                    onClick={() => setActiveTab(tier)}
                    className={`text-xs px-2.5 py-1 rounded-full transition-colors ${
                      isActive
                        ? isAhp
                          ? 'bg-[#002868] text-white'
                          : 'bg-matrix-green text-black'
                        : isAhp
                          ? (ahpDark ? 'bg-white/10 text-gray-300 hover:bg-white/15' : 'bg-gray-100 text-gray-600 hover:bg-gray-200')
                          : 'bg-white/5 text-gray-400 hover:bg-white/10'
                    }`}
                  >
                    {t.label} ({t.hospitals.length.toLocaleString()})
                  </button>
                );
              })}
            </div>

            {/* Tab content */}
            {!activeTier.available || (activeTier.available && activeTier.hospitals.length === 0) ? (
              <div className="text-center py-4">
                {!activeTier.available && (onZipLookup || onDetectLocation) ? (
                  <InlineLocationForm
                    isAhp={isAhp}
                    ahpDark={ahpDark}
                    onDetectLocation={onDetectLocation}
                    onZipLookup={onZipLookup}
                    isLocating={isLocating}
                  />
                ) : (
                  <div className="text-sm text-muted-foreground flex items-center justify-center gap-2 py-2">
                    <MapPin className="w-4 h-4" />
                    {activeTier.unavailableReason || 'No data available'}
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                <RankingTable
                  title={`${Math.min(10, activeTier.cheapest.length)} Cheapest Hospitals`}
                  hospitals={activeTier.cheapest}
                  isAhp={isAhp}
                  ahpDark={ahpDark}
                  colorClass="text-green-600"
                  nationalTotal={nationalTotal}
                  onHospitalClick={handleClick}
                />
                <RankingTable
                  title={`${Math.min(10, activeTier.mostExpensive.length)} Most Expensive Hospitals`}
                  hospitals={activeTier.mostExpensive}
                  isAhp={isAhp}
                  ahpDark={ahpDark}
                  colorClass="text-red-600"
                  nationalTotal={nationalTotal}
                  onHospitalClick={handleClick}
                />
              </div>
            )}
          </div>
        )}

        {/* View on Map link */}
        {hasRankings && !isLoading && (
          <button
            onClick={() => navigate(`/map?scenario=${scenarioId}`)}
            className={`flex items-center gap-1.5 text-xs mt-2 ${isAhp ? (ahpDark ? 'text-[#7da6d4] hover:underline' : 'text-[#002868] hover:underline') : 'text-matrix-green hover:underline'}`}
          >
            <MapIcon className="w-3 h-3" />
            View on Map
          </button>
        )}
      </CardContent>
    </Card>
  );
}

// ── Sub-components ─────────────────────────────────────────────

function TierSummaryCard({
  tier,
  isAhp,
  ahpDark,
  nationalTotal,
  onHospitalClick,
  onDetectLocation,
  onZipLookup,
  isLocating,
}: {
  tier: TierData;
  isAhp: boolean;
  ahpDark: boolean;
  nationalTotal: number;
  onHospitalClick: (h: RankedHospital) => void;
  onDetectLocation?: () => void;
  onZipLookup?: (zip: string) => void;
  isLocating?: boolean;
}) {
  const cheapest = tier.cheapest[0];
  const expensive = tier.mostExpensive[0];

  return (
    <div
      className="rounded-lg px-2.5 py-2"
      style={{
        backgroundColor: isAhp
          ? (ahpDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)')
          : 'rgba(255,255,255,0.03)',
        border: isAhp
          ? (ahpDark ? '1px solid rgba(197,165,90,0.12)' : '1px solid rgba(0,0,0,0.06)')
          : '1px solid rgba(255,255,255,0.06)',
      }}
    >
      {/* Tier header */}
      <div
        className="text-[10px] font-semibold uppercase tracking-wider mb-2 text-center"
        style={{ color: isAhp ? (ahpDark ? '#C5A55A' : '#002868') : undefined }}
      >
        {tier.label}
      </div>

      {!tier.available || tier.hospitals.length === 0 ? (
        !tier.available && (onZipLookup || onDetectLocation) ? (
          <InlineLocationForm
            isAhp={isAhp}
            ahpDark={ahpDark}
            onDetectLocation={onDetectLocation}
            onZipLookup={onZipLookup}
            isLocating={isLocating}
            compact
          />
        ) : (
          <div className="text-center py-3 text-[11px] text-muted-foreground flex flex-col items-center gap-1">
            <MapPin className="w-3.5 h-3.5" />
            {tier.unavailableReason || 'No data'}
          </div>
        )
      ) : (
        <>
          {/* Cheapest */}
          {cheapest && (
            <button
              onClick={() => onHospitalClick(cheapest)}
              className="w-full text-left mb-1.5"
            >
              <div className="text-[9px] font-semibold uppercase tracking-wider text-green-600 flex items-center gap-0.5 mb-0.5">
                <TrendingDown className="w-2.5 h-2.5" /> Cheapest
              </div>
              <div className={`text-[11px] font-medium truncate ${isAhp ? (ahpDark ? 'text-[#f0ebe3]' : 'text-gray-900') : 'text-white'}`}>
                {cheapest.hospitals.name}
              </div>
              <div className="text-[10px] text-muted-foreground truncate">
                {cheapest.hospitals.city}, {cheapest.hospitals.state}
              </div>
              <div className="flex items-center gap-1 mt-0.5">
                <span className={`text-xs font-semibold ${isAhp ? '' : 'font-mono text-white'}`}>
                  {cheapest.matched_codes < cheapest.total_codes ? '~' : ''}${Math.round(cheapest.blendedPrice).toLocaleString()}
                </span>
                <MarkupBadge markup={getBlendedMarkup(cheapest, nationalTotal)} />
              </div>
            </button>
          )}

          {/* Divider */}
          <div className={`border-t my-1.5 ${isAhp ? (ahpDark ? 'border-white/5' : 'border-gray-100') : 'border-white/5'}`} />

          {/* Most expensive */}
          {expensive && (
            <button
              onClick={() => onHospitalClick(expensive)}
              className="w-full text-left mb-1.5"
            >
              <div className="text-[9px] font-semibold uppercase tracking-wider text-red-600 flex items-center gap-0.5 mb-0.5">
                <TrendingUp className="w-2.5 h-2.5" /> Most Expensive
              </div>
              <div className={`text-[11px] font-medium truncate ${isAhp ? (ahpDark ? 'text-[#f0ebe3]' : 'text-gray-900') : 'text-white'}`}>
                {expensive.hospitals.name}
              </div>
              <div className="text-[10px] text-muted-foreground truncate">
                {expensive.hospitals.city}, {expensive.hospitals.state}
              </div>
              <div className="flex items-center gap-1 mt-0.5">
                <span className={`text-xs font-semibold ${isAhp ? '' : 'font-mono text-white'}`}>
                  {expensive.matched_codes < expensive.total_codes ? '~' : ''}${Math.round(expensive.blendedPrice).toLocaleString()}
                </span>
                <MarkupBadge markup={getBlendedMarkup(expensive, nationalTotal)} />
              </div>
            </button>
          )}

          {/* Hospital count */}
          <div className="text-[10px] text-muted-foreground text-center mt-1">
            {tier.hospitals.length.toLocaleString()} hospital{tier.hospitals.length !== 1 ? 's' : ''}
          </div>
        </>
      )}
    </div>
  );
}

function getBlendedMarkup(h: RankedHospital, nationalTotal: number): number {
  if (nationalTotal <= 0) return 0;
  return ((h.blendedPrice - nationalTotal) / nationalTotal) * 100;
}

function RankingTable({
  title,
  hospitals,
  isAhp,
  ahpDark,
  colorClass,
  nationalTotal,
  onHospitalClick,
}: {
  title: string;
  hospitals: RankedHospital[];
  isAhp: boolean;
  ahpDark: boolean;
  colorClass: string;
  nationalTotal: number;
  onHospitalClick: (h: RankedHospital) => void;
}) {
  const navigate = useNavigate();
  return (
    <div>
      <h4 className={`text-xs font-semibold uppercase tracking-wider mb-2 ${colorClass}`}>{title}</h4>
      <div className="space-y-1">
        {hospitals.map((h, i) => {
          const blendedMarkup = getBlendedMarkup(h, nationalTotal);
          const isPartial = h.matched_codes < h.total_codes;
          return (
            <button
              key={h.hospital_id}
              onClick={() => onHospitalClick(h)}
              className={`w-full flex items-center gap-2 p-2 rounded-lg text-left transition-colors ${isAhp ? (ahpDark ? 'hover:bg-white/5' : 'hover:bg-gray-50') : 'hover:bg-white/5'}`}
            >
              <span className={`text-xs font-bold w-5 text-center ${colorClass}`}>#{i + 1}</span>
              <div className="flex-1 min-w-0">
                <div
                  className={`text-sm font-medium truncate ${isAhp ? '' : 'text-white'}`}
                  style={isAhp ? { color: ahpDark ? '#f0ebe3' : '#1B1B1B' } : undefined}
                >
                  {h.hospitals.name}
                </div>
                <div className="text-[11px] text-muted-foreground">
                  {h.hospitals.city}, {h.hospitals.state}
                  <span className="mx-1">&middot;</span>
                  {h.matched_codes}/{h.total_codes} codes
                </div>
              </div>
              <a
                href={`/hospital/${h.hospital_id}`}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  navigate(`/hospital/${h.hospital_id}`);
                }}
                title="View hospital profile"
                className={`shrink-0 p-1 rounded transition-colors ${isAhp ? (ahpDark ? 'hover:bg-white/10 text-muted-foreground hover:text-[#f0ebe3]' : 'hover:bg-gray-100 text-muted-foreground hover:text-gray-700') : 'hover:bg-white/10 text-gray-500 hover:text-matrix-green'}`}
              >
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
              <div className="text-right shrink-0">
                <div className={`text-sm font-semibold ${isAhp ? '' : 'font-mono text-white'}`} style={isAhp ? { color: ahpDark ? '#f0ebe3' : '#1B1B1B' } : undefined}>
                  {isPartial ? '~' : ''}${Math.round(h.blendedPrice).toLocaleString()}
                </div>
                <MarkupBadge markup={blendedMarkup} />
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function InlineLocationForm({
  isAhp,
  ahpDark,
  onDetectLocation,
  onZipLookup,
  isLocating,
  compact,
}: {
  isAhp: boolean;
  ahpDark: boolean;
  onDetectLocation?: () => void;
  onZipLookup?: (zip: string) => void;
  isLocating?: boolean;
  compact?: boolean;
}) {
  const [zipInput, setZipInput] = useState('');

  const handleSubmit = () => {
    if (/^\d{5}$/.test(zipInput) && onZipLookup) {
      onZipLookup(zipInput);
    }
  };

  if (compact) {
    // Small form for tier summary cards
    return (
      <div className="flex flex-col items-center gap-1.5 py-1">
        <div className="flex items-center gap-1">
          <Input
            type="text"
            inputMode="numeric"
            placeholder="ZIP"
            value={zipInput}
            onChange={(e) => setZipInput(e.target.value.replace(/\D/g, '').slice(0, 5))}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSubmit(); }}
            className={`w-16 h-7 text-xs text-center ${isAhp ? '' : 'bg-black/60 border-matrix-green/30 text-white placeholder:text-gray-500'}`}
            style={isAhp ? { borderColor: ahpDark ? 'rgba(197,165,90,0.3)' : 'rgba(27,27,27,0.2)' } : undefined}
          />
          <Button
            variant="default"
            size="sm"
            onClick={handleSubmit}
            disabled={!/^\d{5}$/.test(zipInput) || isLocating}
            className={`h-7 text-[10px] px-2 ${isAhp
              ? 'bg-[#002868] text-white hover:bg-[#001a4a]'
              : 'bg-matrix-green text-black hover:bg-matrix-green/80'
            }`}
          >
            Go
          </Button>
        </div>
        {onDetectLocation && (
          <button
            onClick={onDetectLocation}
            disabled={isLocating}
            className="flex items-center gap-1 text-[10px] hover:underline disabled:opacity-50"
            style={{ color: ahpDark ? '#7da6d4' : isAhp ? '#002868' : '#22c55e' }}
          >
            {isLocating ? (
              <Loader2 className="w-2.5 h-2.5 animate-spin" />
            ) : (
              <Navigation className="w-2.5 h-2.5" />
            )}
            {isLocating ? 'Locating...' : 'Use GPS'}
          </button>
        )}
      </div>
    );
  }

  // Larger form for expanded tab view
  return (
    <div className="flex flex-col items-center gap-2 py-2">
      <p className="text-xs text-muted-foreground">Enter your ZIP code to see local data</p>
      <div className="flex items-center gap-2">
        <Input
          type="text"
          inputMode="numeric"
          placeholder="ZIP code"
          value={zipInput}
          onChange={(e) => setZipInput(e.target.value.replace(/\D/g, '').slice(0, 5))}
          onKeyDown={(e) => { if (e.key === 'Enter') handleSubmit(); }}
          className={`w-24 h-8 text-sm ${isAhp ? '' : 'bg-black/60 border-matrix-green/30 text-white placeholder:text-gray-500'}`}
          style={isAhp ? { borderColor: ahpDark ? 'rgba(197,165,90,0.3)' : 'rgba(27,27,27,0.2)' } : undefined}
        />
        <Button
          variant="default"
          size="sm"
          onClick={handleSubmit}
          disabled={!/^\d{5}$/.test(zipInput) || isLocating}
          className={`h-8 text-xs ${isAhp
            ? 'bg-[#002868] text-white hover:bg-[#001a4a]'
            : 'bg-matrix-green text-black hover:bg-matrix-green/80'
          }`}
        >
          Search
        </Button>
        {onDetectLocation && (
          <>
            <span className="text-xs text-muted-foreground">or</span>
            <Button
              variant="outline"
              size="sm"
              onClick={onDetectLocation}
              disabled={isLocating}
              className={`h-8 text-xs gap-1.5 ${isAhp
                ? 'border-[#002868]/30 text-[#002868] hover:bg-[#002868]/5'
                : 'border-matrix-green/30 text-matrix-green hover:bg-matrix-green/10'
              }`}
              style={ahpDark ? { borderColor: 'rgba(125,166,212,0.3)', color: '#7da6d4' } : undefined}
            >
              {isLocating ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Navigation className="w-3.5 h-3.5" />
              )}
              {isLocating ? 'Locating...' : 'Use My Location'}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

function MarkupBadge({ markup }: { markup: number }) {
  const isNegative = markup < 0;
  const color = markupBadgeColor(markup);

  return (
    <span
      className="text-[10px] font-medium px-1.5 py-0.5 rounded-full inline-block"
      style={{ backgroundColor: `${color}20`, color }}
    >
      {isNegative ? '' : '+'}{markup.toFixed(0)}%
    </span>
  );
}
